import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowCounterClockwise } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useAbortCancelSubscription,
  useStripeSubscription,
} from 'src/api/queries';
import { Button, NotificationBox } from 'src/components';
import { NotificationBanner } from 'src/components/NotificationBanner';
import { formatDate } from 'src/utils/formatDate';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';

export const SubscriptionCanceledNotification = () => {
  const { t, i18n } = useLingui();
  const subscription = useStripeSubscription();
  const abortCancelSubscription = useAbortCancelSubscription();

  if (subscription.isLoading || subscription.isError) return null;

  if (!subscriptionHelpers.isCancelled(subscription.data)) return null;

  const info = subscription.data.paymentInformation;

  const cancellationDate = formatDate(info.cancellationAt, {
    locale: i18n.locale,
  });

  return (
    <NotificationBanner>
      <div className="flex flex-col gap-16">
        <Typography variant="heading-subtle-lg" className="my-0">
          <Trans>Your subscription ends {cancellationDate}</Trans>
        </Typography>

        <Typography variant="interface-md" className="my-0 leading-150">
          {subscription.data.managementType == 'seats' ? (
            <Trans>
              All users will lose access to the included features, and
              you&apos;ll no longer be charged. Undo your cancelation to keep
              Connect.
            </Trans>
          ) : (
            <Trans>
              You will lose access to the included features, and you&apos;ll no
              longer be charged. Undo your cancelation to keep Connect.
            </Trans>
          )}
        </Typography>

        {abortCancelSubscription.isError && (
          <NotificationBox
            error={abortCancelSubscription.error}
            title={t`Something went wrong`}
          >
            <Trans>
              We couldn&apos;t undo your cancelation. Please try again later or
              contact support if the problem persists.
            </Trans>
          </NotificationBox>
        )}

        <Button
          variant="tertiary"
          size="medium"
          className="mt-8 w-full xs:w-fit"
          loading={abortCancelSubscription.isPending}
          onClick={() => {
            abortCancelSubscription.mutate();
          }}
          analytics={{
            text: 'Undo cancelation',
            location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
            action: 'undo cancelation',
          }}
        >
          <ArrowCounterClockwise />
          <span>
            <Trans>Undo cancelation</Trans>
          </span>
        </Button>
      </div>
    </NotificationBanner>
  );
};
